import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, ErrorState, LoadingState } from "@/components/ui";
import { reflectiveApi } from "@/lib/api";

// PLAN-2 M5 — the in-panel render of a pre-conference brief. Scope-honesty leads;
// three sections (where things stand / what's open / talking points), each point
// anchored to its record; "save as document" reuses generate_doc propose->confirm.
function Section({ title, points, testid }) {
  if (!points?.length) return null;
  return (
    <div data-testid={testid} className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</p>
      <ul className="space-y-1">
        {points.map((point, index) => (
          <li key={index} className="text-sm leading-6 text-slate-800">
            {point.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BriefBlock({ teacherId, teacherName, onSaveAsDocument }) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["mira-pre-conference-brief", teacherId],
    queryFn: () => reflectiveApi.preConferenceBrief(teacherId).then((res) => res.data.brief),
    enabled: Boolean(teacherId),
    retry: false,
  });

  if (query.isLoading) return <LoadingState message={t("brief.loading")} />;
  if (query.isError) return <ErrorState title={t("brief.errorTitle")} message={t("brief.errorMessage")} />;

  const brief = query.data || {};

  return (
    <div data-testid="brief-block" className="space-y-3">
      {teacherName ? <div className="text-sm font-semibold text-slate-900">{teacherName}</div> : null}
      {brief.basis_line ? (
        <p data-testid="brief-basis" className="text-xs text-slate-500">
          {brief.basis_line}
        </p>
      ) : null}

      <Section title={t("brief.whereThingsStand")} points={brief.where_things_stand} testid="brief-where" />
      <Section title={t("brief.whatsOpen")} points={brief.whats_open} testid="brief-open" />
      <Section title={t("brief.talkingPoints")} points={brief.talking_points} testid="brief-talking" />

      {onSaveAsDocument ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          data-testid="brief-save"
          onClick={() => onSaveAsDocument(brief)}
        >
          {t("brief.saveAsDocument")}
        </Button>
      ) : null}
    </div>
  );
}

export default BriefBlock;
